/**
 * A repository's name every machine can match (D-0010, answered (a): the git remote URL, normalised).
 *
 * A path cannot be it: every machine holds its checkout somewhere else, and the paths work areas
 * stored (`/repos/featit/featit-app`) are a container mount ADR-079 removed (I-0345). The remote is
 * the one thing every clone of a repository agrees on, once the spelling differences are dropped:
 * scheme, user and password, port, a trailing `.git` or `/`, and the host's case.
 * `git@gitlab.com:evoye/x.git`, `ssh://git@gitlab.com:22/evoye/x` and `https://gitlab.com/evoye/x`
 * are one repository, `gitlab.com/evoye/x`.
 *
 * ⛔ Credentials never survive: an `https://user:token@host/...` remote normalises to `host/...`, so
 * the identity can be stored and printed. A remote that is a local path names a place, not a
 * repository, and has no identity (null).
 */
export function normalizeRepositoryRemote(remote: string | null | undefined): string | null {
  const value = (remote ?? '').trim();
  if (!value) return null;

  let host: string;
  let repoPath: string;
  const url = /^[a-z][a-z0-9+.-]*:\/\/(?:[^@/]*@)?([^/:]+)(?::\d*)?\/(.+)$/i.exec(value);
  const scpLike = /^(?:[^@/:]+@)?([^/:]+):(?!\/\/)(.+)$/.exec(value);
  if (url) {
    if (/^file:/i.test(value)) return null;
    [, host, repoPath] = url;
  } else if (scpLike && !/^[a-z]:[\\/]/i.test(value)) {
    [, host, repoPath] = scpLike;
  } else {
    return null;
  }

  const cleanPath = repoPath
    .replace(/^\/+/, '')
    .replace(/[\\/]+$/, '')
    .replace(/\.git$/i, '')
    .replace(/[\\/]+$/, '');
  if (!cleanPath) return null;
  return `${host.toLowerCase()}/${cleanPath}`;
}
